<template>
    <div id="content">
        <router-link :to="$route.path.split('/').slice(0, -1).join('/')" class="back-link"><i class="bi bi-chevron-left"></i> {{ $t('plans.plan.back') }}</router-link>

        <div class="admin-card plan-head">
            <div class="plan-head-main">
                <div class="admin-section-label">
                    <i class="bi bi-piggy-bank"></i>
                    {{ $t('plans.plan.title') }}
                </div>
                <h1 class="plan-name">{{ plan.title }}</h1>
                <h3 class="plan-price text-gradient">{{ plan.price }} ₸ / {{ cycles[plan.cycle] }}</h3>
            </div>
            <div class="plan-head-actions">
                <button :disabled="paying" @click="paymentLink('garantpay')" class="btn-purple">
                    <i class="bi" :class="plan.id === user.pl_id ? 'bi-arrow-clockwise' : 'bi-cart-plus'"></i>
                    <span>{{ $t('plans.plan.payKz') }}</span>
                </button>
                <button :disabled="paying" @click="paymentLink('stripe')" class="btn-ghost">
                    <i class="bi bi-globe2"></i>
                    <span>International</span>
                </button>
            </div>
        </div>

        <div class="row g-4 mt-1">
            <div v-for="m in modules" :key="m.id" class="col-md-4 col-lg-3">
                <div v-if="Object.values(planLimits).some(i => i.module_id === m.id)" class="admin-card plan-module-card" :class="{ off: !m.status }">
                    <div class="plan-module-header">
                        <span class="plan-module-name">{{ m.description }}</span>
                        <span v-if="!m.status" class="admin-pill admin-pill-muted">Off</span>
                        <span v-else class="admin-pill admin-pill-purple">On</span>
                    </div>
                    <div class="plan-module-body">
                        <template v-if="m.status">
                            <div v-for="l in Object.values(plan.limits).filter(limit => limit.module_id == m.id)" :key="l.function_id" class="plan-module-limit">
                                <template v-if="l.function_limit && l.function_limit != 'null'">
                                    <i class="bi bi-check-circle-fill plan-limit-on"></i>
                                    <span>{{ l.name }}<b v-if="!isNaN(l.function_limit)"> — {{ l.function_limit }}</b></span>
                                </template>
                                <template v-else>
                                    <i class="bi bi-x-circle plan-limit-off"></i>
                                    <span>{{ l.name }}</span>
                                </template>
                            </div>
                        </template>
                        <div v-else class="plan-module-disabled">
                            {{ $t('plans.plan.moduleDisabled') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { cycles, tsToHuman } from '../../../lib/utils'

export default {
    data:() => ({
        plan: {},
        limits: {},
        user: {},
        cycles: cycles.map(c => c.text),
        modules: [],
        planLimits: '',
        paying: false
    }),
    async mounted() {
        await this.getPlan()

        this.$emit('setPageTitle', this.plan.title ?? this.$t('plans.plan.title'))

        this.user = await this.getUser()
        this.modules = await this.getPlanModules()
        this.limits = Object.fromEntries(
            Object.values(this.plan.limits).map(obj => [obj["module_id"], obj])
        )
    },
    methods: {
        async getPlan() {
            let response = await this.$api.getPlanWithLimits(this.$route.params.id)
            if(response.result === 'SUCCESS') {
                this.plan = response.data
                this.planLimits = this.plan.limits
            } else {
                this.$notify({
                    text: this.$t('plans.plan.fetchError'),
                    type: 'error'
                })
            }
        },
        async getUser() {
            let r = await this.$api.getUser()
            if(r.result === 'SUCCESS') {
                return r.data
            } else {
                this.$notify({
                    text: r.msg,
                    type: r.result.toLowerCase()
                })
                return false
            }
        },
        async getPlanModules() {
            let r = await this.$api.getPlanModules(this.plan.id)
            if(r.result === 'SUCCESS') {
                return r.data
            } else {
                this.$notify({
                    text: r.msg,
                    type: r.result.toLowerCase()
                })
                return []
            }
        },
        async paymentLink(provider = 'garantpay') {
            if(!this.user.id || !this.plan.id || this.paying) return false
            this.paying = true

            try {
                let response = await this.$api.purchase(this.plan.price, this.plan.id, this.user.id, provider)

                if(response.result === 'ERROR') {
                    this.$notify({
                        text: response.msg || response.message || this.$t('plans.plan.paymentError'),
                        type: 'error'
                    })
                    return false
                }

                // Stripe response shape: { provider: 'stripe', checkout_url, transaction_id }
                if(response?.data?.checkout_url) {
                    window.location.href = response.data.checkout_url
                    return
                }

                // GarantPay: переход в текущей вкладке. window.open после
                // await теряет пользовательский жест и блокируется
                // браузером — оплата молча не открывалась. Возврат в кабинет
                // обеспечивает returnUrl на стороне провайдера.
                if(response?.data?.url) {
                    window.location.href = response.data.url
                } else {
                    this.$notify({
                        text: this.$t('plans.plan.paymentError'),
                        type: 'error'
                    })
                }
            } finally {
                this.paying = false
            }
        }
    }
}
</script>

<style scoped>
.back-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    text-decoration: none;
    margin-bottom: 14px;
    transition: color 0.15s ease;
}
.back-link:hover {
    color: #6d28d9;
}
.back-link i {
    font-size: 14px;
}

.plan-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 24px;
    flex-wrap: wrap;
}

.plan-head-main {
    flex: 1;
    min-width: 240px;
}

.plan-name {
    font-size: 26px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 4px 0;
    letter-spacing: -0.01em;
}

.plan-price {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    width: fit-content;
}

.plan-head-actions {
    display: flex;
    gap: 10px;
}

.plan-module-card {
    height: 100%;
    padding: 18px 20px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.plan-module-card.off {
    background: #f8fafc;
}

.plan-module-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 14px;
}

.plan-module-name {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
}

.plan-module-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.plan-module-limit {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 13px;
    color: #1e293b;
}

.plan-limit-on {
    color: #16a34a;
    font-size: 16px;
    flex-shrink: 0;
}
.plan-limit-off {
    color: #cbd5e1;
    font-size: 16px;
    flex-shrink: 0;
}

.plan-module-limit b {
    color: #6d28d9;
    font-weight: 600;
}

.plan-module-disabled {
    text-align: center;
    color: #94a3b8;
    font-size: 13px;
    padding: 12px 0;
}
</style>
